import React, { ChangeEvent, FC, useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { useFormik } from 'formik';

import { API, ResponseStatus } from 'app/api';
import { FormControl } from 'app/design-system/components/FormControl';
import { MIN_LEN_PHONE_NUMBER } from 'app/design-system/components/PhoneInput/constants';
import { getDialCode, removeNotNumberCharacter } from 'app/design-system/components/PhoneInput/phone-input-helpers';
import { StepperContext } from 'app/design-system/components/Stepper/context';
import { useTranslation } from 'app/lang';
import usePhoneData from 'app/lib/region/usePhoneData';
import { RegContext } from 'app/modules/Signing/context';
import { createSigningQuery } from 'app/modules/Signing/utils/signing-modals-query';
import { createGaEvent, dispatchGaEvent } from 'app/packages/analytics';
import { CodeError } from 'app/widgets/CodeConfirmation';
import PhoneInput from 'app/widgets/InputsWithAnalytics/PhoneInput';

import { REG_DATA_INITIAL_STATE } from '../../../../constants';
import { validatePhoneNumber } from '../../../../utils/validation/signing-validate';

import dictionary from './FirstStepForm.dictionary.json';

import styles from './FirstStepForm.module.scss';

type FormValues = {
  phoneNumber: string;
};

export const FirstStepForm: FC = () => {
  const { t } = useTranslation();
  const { replace } = useHistory();
  const { regData, setRegData } = useContext(RegContext);
  const { activeStep, forwardStep } = useContext(StepperContext);
  const validate = validatePhoneNumber(t);
  const phoneData = usePhoneData();

  const { requisite: phone, type: requestType, disabled } = regData;
  const initialValues: FormValues = { phoneNumber: phoneData.dialCode };

  useEffect(() => {
    setRegData((prevState) => ({ ...prevState, regStep: activeStep }));
  }, []);

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setErrors,
    setFieldValue,
  } = useFormik({
    initialValues,
    validateOnChange: false,
    validate,
    onSubmit: async (values: FormValues) => {
      setRegData((prevState) => ({ ...prevState, disabled: true }));
      const phoneNumber = removeNotNumberCharacter(values.phoneNumber);

      await API.registration.getCheckCode({ phoneNumber })
        .then((response) => {
          if (response.status === ResponseStatus.SUCCESS) {
            dispatchGaEvent(createGaEvent({ event: 'signInNumberInputConfirm' }));
            setRegData((prevState) => ({ ...prevState, ...response.data }));
            return forwardStep();
          }

          // TODO: https://jira.tennisi.it/browse/SITE-742
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const responseData = response.data as any;

          const requisite = phone === '' ? values.phoneNumber : phone;
          const type = requestType === '' ? 'sms' : requestType;

          if (response.status === ResponseStatus.UNPROCESSABLE_ENTITY) {
            if (responseData?.hasOwnProperty('errors')) {
              return setErrors({ phoneNumber: t(dictionary.errorPhoneNumber) });
            }

            const { resendDate, serverTime, attemptsLeft, attemptsTotal, detail } = responseData;

            if (resendDate && serverTime) {
              const newState = {
                requisite, type,
                resendDate, serverTime,
                attemptsLeft, attemptsTotal,
                codeError: CodeError.NOT_SEND,
              };

              setRegData((prevState) => ({ ...prevState, ...newState }));
              return forwardStep();
            }

            if (detail) {
              return setErrors({ phoneNumber: detail });
            }
          }
          if (response.status === ResponseStatus.TOO_MANY_REQUESTS) {
            const { resendDate, serverTime } = responseData;

            const newState = {
              requisite, type,
              resendDate, serverTime,
              codeError: CodeError.NOT_SEND,
              attemptsLeft: REG_DATA_INITIAL_STATE.attemptsLeft,
              attemptsTotal: REG_DATA_INITIAL_STATE.attemptsTotal,
            };

            setRegData((prevState) => ({ ...prevState, ...newState }));
            setErrors({ phoneNumber: t(dictionary.errorText) });
            return forwardStep();
          }
        })
        .finally(() => setRegData((prevState) => ({ ...prevState, disabled: false })));
    },
  });

  useEffect(() => {
    if (regData.requisite !== '') {
      setFieldValue('phoneNumber', regData.requisite);
    }
  }, []);

  const fieldInfo = errors.phoneNumber ?? t(dictionary.phoneFieldInfo);
  const error = Boolean(errors.phoneNumber) && touched.phoneNumber;

  const handleOnChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = removeNotNumberCharacter(event.target.value);
    const currentNumber = removeNotNumberCharacter(values.phoneNumber);
    const newNumber = value.slice(currentNumber.length);

    // Todo https://jira.tennisi.it/browse/WEB-1654
    const isCopyPasteFullNumber = value.length - currentNumber.length >= MIN_LEN_PHONE_NUMBER;

    if (isCopyPasteFullNumber) {
      const currentNumberDialCode = getDialCode(currentNumber);
      const newNumberDialCode = getDialCode(newNumber);

      if (currentNumberDialCode === newNumberDialCode) {
        setRegData((prevState) => ({ ...prevState, requisite: newNumber }));
        setFieldValue('phoneNumber', newNumber);
        sessionStorage.setItem('phone', newNumber);

        return;
      }
    }

    setRegData((prevState) => ({ ...prevState, requisite: value }));
    setFieldValue('phoneNumber', value);
    sessionStorage.setItem('phone', value);
  }, [values.phoneNumber]);

  const handleFieldFocus = useCallback(() => {
    setErrors({});
  }, []);

  const fieldInfoClassNames = classNames(styles.fieldInfo, {
    [styles.error]: error,
  });

  const handleEnterWithPassword = useCallback(() => {
    replace({ search: createSigningQuery('login') });
  }, []);

  const enterWithPassword = (
    <button data-test="ModalLogin-button-enterPassword"
            data-ga-event="SignEnterPassword"
            data-ga-event-value="headerMenuPassword"
            className={styles.enterPassword}
            onClick={handleEnterWithPassword}>
      {t(dictionary.buttonText)}
    </button>
  );

  return (
    <form id={`registrationFormStep_${activeStep}`}
          noValidate
          onSubmit={handleSubmit}
          data-test={`registrationFormStep_${activeStep}`}>
      <FormControl label={t(dictionary.phoneLabel)}
                   data-test="RegistrationModal-regLabel"
                   linkNote={enterWithPassword}>
        <PhoneInput name="phoneNumber"
                    value={values.phoneNumber}
                    placeholder={phoneData.placeholder}
                    onChange={handleOnChange}
                    error={error}
                    autoComplete="on"
                    disabled={disabled}
                    autoFocus
                    onFocus={handleFieldFocus}
                    data-ga-event="signInNumberInput"
                    data-test="Modal-registration-phoneInput" />
        <span className={fieldInfoClassNames}
              data-test="field-info">
          {fieldInfo}
        </span>
      </FormControl>
    </form>
  );
};
